import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { VoidFunctionComponent } from 'react';
import type { InjectedRouter } from 'react-router';

import { window } from '../../globals';
import Flex from '../generic/Flex';
import { TooltipButton } from '../generic/Button';
import Icon, { icons, IconText } from '../generic/Icon';
import * as utils from '../../utils';
import { Action, ResourceType } from '../../../typings/permissions';
import OrganizationsModule from '../../redux/organizations';
import { useDialogContext } from '../../contexts/DialogContext';
import { useDispatchOnMount } from '../../hooks';
import { DISABLE_ADD_PROJECT_BUTTON } from '../../../shared/featureFlags/flagNames';
import { isFeatureEnabled } from '../../helpers/features';
import { PREMIUM_PER_REVISION_PLAN_NAME } from '../../../shared/subscriptions';
import RevisionUsageBadge from './RevisionUsageBadge';
import TeamSelectionMenu from '../shared/teams/TeamSelectionMenu';
import * as ProjectsV2Store from '../../redux/projectsV2';

type BrowseProjectNavProps = {
  router: InjectedRouter;
};

const BrowseProjectNav: VoidFunctionComponent<BrowseProjectNavProps> = ({
  router,
}) => {
  const dispatch = useDispatch();
  const { onAlert, onConfirm } = useDialogContext();
  const currentlyScanning = useSelector(OrganizationsModule.selectors.getCurrentlyScanning);
  const isAddProjectButtonDisabled = isFeatureEnabled(DISABLE_ADD_PROJECT_BUTTON);
  const isPerRevisionSubscriber = window.user?.organization.stripe_plan === PREMIUM_PER_REVISION_PLAN_NAME;

  const canPolicyScanAll = window.hasPermission({
    action: Action.Edit,
    resourceType: ResourceType.ProjectAny,
  });

  useDispatchOnMount(OrganizationsModule.actions.fetchCurrentlyScanning);

  const onImportProjectClicked = useCallback(() => {
    if (window.hasPermission({ action: Action.Create, resourceType: ResourceType.Project })) {
      router.push('/projects/import');
      return;
    }

    onAlert({
      title: 'Import Project',
      body: 'Please ask an organization administrator to import a new project.',
    });
  }, [onAlert, router]);

  const onScanAllClicked = useCallback(() => {
    if (canPolicyScanAll) {
      onConfirm({
        title: 'Run policy scan on all projects',
        body: 'Are you sure you want to queue policy scans for ALL projects?',
        onConfirm: () => {
          dispatch(OrganizationsModule.actions.scan());
          onAlert({
            title: 'Running policy scan on all of your projects',
            body: 'All of your projects are being policy scanned. Please check back in a few minutes to see any changes.',
          });
        } });
    } else {
      onAlert({
        title: 'Failed to Start Scan',
        body: 'Please ask a project manager to start a policy scan',
      });
    }
  }, [dispatch, onAlert, onConfirm]);

  return (
    <>
      {(window.user && window.user.organization) && (
        <Flex justify="flex-start">
          <h1 style={{ margin: '0 6px 0 0' }}>
            {utils.trimString(window.user.organization.title, 50)}
            {' '}
            <span className="header-divider">
              <Icon icon={icons.angleRight} />
            </span>
          </h1>
          <TeamSelectionMenu onChange={() => {
            dispatch(ProjectsV2Store.actions.clearSelection());
            dispatch(ProjectsV2Store.actions.preFetchLoading());
            dispatch(ProjectsV2Store.actions.fetchProjects());
          }} />
        </Flex>
      )}
      <div className="bottom-navigation animated fadeInUp margin-bottom-lg projects-nav-buttons">
        <Flex justify="space-between">
          <Flex justify="flex-start">
            {window.hasPermission({ action: Action.Create, resourceType: ResourceType.Project }) && (
            <span id="btn-import-bulk" className="margin-right-sm">
              <TooltipButton
                icon={icons.plusCircle}
                size="large"
                type="primary"
                onClick={onImportProjectClicked}
                isDisabled={isAddProjectButtonDisabled}
                showTooltip={isAddProjectButtonDisabled}
                tooltip="Disabled by your admins"
              >
                Add Projects
              </TooltipButton>
            </span>
            )}
            <TooltipButton
              type="outline-light"
              placement="right"
              size="large"
              isDisabled={currentlyScanning || !canPolicyScanAll}
              showTooltip={!canPolicyScanAll}
              onClick={onScanAllClicked}
              tooltip="You do not have permission."
          >
              <IconText
                marginAmount="8px"
                icon={currentlyScanning ? icons.circleO : icons.refresh}
                hasSpin={currentlyScanning}
                text={currentlyScanning ? 'Running policy scans' : 'Run policy scans'}
            />
            </TooltipButton>
          </Flex>
          {isPerRevisionSubscriber &&
            <RevisionUsageBadge />}
        </Flex>
      </div>
    </>
  );
};

export default BrowseProjectNav;
